import { Download, Search } from "lucide-react";
import {
  PRODUCT_OPTIONS, USAGE_OPTIONS, FRUSTRATION_OPTIONS, FEATURE_OPTIONS,
  DEVICE_OPTIONS, EVAL_INTEREST_OPTIONS,
} from "@/lib/iqQuestions";

const FILTERS = [
  { key: "product", label: "Product", options: PRODUCT_OPTIONS },
  { key: "usage", label: "Usage", options: USAGE_OPTIONS },
  { key: "frustration", label: "Frustration", options: FRUSTRATION_OPTIONS },
  { key: "feature", label: "Feature", options: FEATURE_OPTIONS },
  { key: "device", label: "Device", options: DEVICE_OPTIONS },
  { key: "eval_interest", label: "Eval Interest", options: EVAL_INTEREST_OPTIONS },
  { key: "source", label: "Source", options: ["direct", "homepage", "card", "newsletter"] },
];

function selectCls() {
  return "bg-[#1a1a1a] border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#ff2d78] transition-colors appearance-none cursor-pointer";
}

export function IQAdminTable({ items, total, filters, setFilters, onExport }) {
  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="iq-admin-table-section" className="mt-8">
      <div className="flex flex-wrap items-center gap-2.5">
        {FILTERS.map((f) => (
          <select key={f.key} data-testid={`iq-admin-filter-${f.key}`} value={filters[f.key] || ""} onChange={setFilter(f.key)} className={selectCls()}>
            <option value="">{f.label}: All</option>
            {f.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ))}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="iq-admin-search-input"
            value={filters.search || ""}
            onChange={setFilter("search")}
            placeholder="Search name or email"
            className="bg-[#1a1a1a] border border-zinc-700 rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff2d78] transition-colors"
          />
        </div>
        <button
          data-testid="iq-admin-export-csv"
          onClick={onExport}
          className="inline-flex items-center gap-1.5 font-body text-xs font-medium bg-[#ff2d78] text-white px-4 py-2 rounded-lg hover:bg-white hover:text-black transition-colors ml-auto"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>

      <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-zinc-500 mt-4">{total} matching registration{total === 1 ? "" : "s"}</p>

      <div className="overflow-x-auto mt-3 border border-zinc-800 rounded-xl">
        <table data-testid="iq-admin-table" className="w-full text-left text-xs">
          <thead>
            <tr className="bg-[#111] text-zinc-500 font-mono uppercase tracking-wider text-[10px]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Products</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Eval Interest</th>
              <th className="px-4 py-3">Updates</th>
              <th className="px-4 py-3">Research</th>
              <th className="px-4 py-3">Subs</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-600 font-body">No registrations match these filters yet.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} data-testid={`iq-admin-row-${r.id}`} className="border-t border-zinc-800 text-zinc-300">
                <td className="px-4 py-3 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{r.first_name}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 max-w-[200px] truncate" title={(r.products || []).join(", ")}>{(r.products || []).join(", ") || "\u2014"}</td>
                <td className="px-4 py-3 capitalize">{r.source || "\u2014"}</td>
                <td className="px-4 py-3 max-w-[160px] truncate">{r.eval_interest || "\u2014"}</td>
                <td className="px-4 py-3">{r.consent_updates ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{r.consent_research ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{r.submission_count || 1}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
